import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Trash2, AlertTriangle, ImageIcon, CheckCircle, Download } from 'lucide-react';
import { toast } from 'sonner';

interface Job {
  id: string;
  job_name: string;
  images_generated: number;
  downloaded_at: string | null;
}

interface OrganizedDeleteSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobs: Job[];
  onDeleteJobs: (jobIds: string[]) => Promise<void>;
}

export const OrganizedDeleteSelector: React.FC<OrganizedDeleteSelectorProps> = ({
  open,
  onOpenChange,
  jobs,
  onDeleteJobs
}) => {
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const toggleJob = (jobId: string) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
  };

  const selectAll = () => {
    setSelectedJobs(new Set(jobs.map(j => j.id)));
  };

  const clearAll = () => {
    setSelectedJobs(new Set());
  };

  const selectDownloaded = () => {
    const downloadedJobIds = jobs
      .filter(j => j.downloaded_at)
      .map(j => j.id);
    setSelectedJobs(new Set(downloadedJobIds));
  };

  const downloadedCount = jobs.filter(j => j.downloaded_at).length;

  const selectedJobsData = useMemo(() => {
    return jobs.filter(j => selectedJobs.has(j.id));
  }, [jobs, selectedJobs]);

  const totalImages = useMemo(() => {
    return selectedJobsData.reduce((sum, j) => sum + (j.images_generated || 0), 0);
  }, [selectedJobsData]);

  const requiresTextConfirm = selectedJobs.size >= 20;
  const showWarning = selectedJobs.size >= 10;
  const isCritical = selectedJobs.size >= 50;

  const canDelete = useMemo(() => {
    if (selectedJobs.size === 0) return false;
    if (!confirmChecked) return false;
    if (requiresTextConfirm && deleteConfirmText !== 'DELETE') return false;
    return true;
  }, [selectedJobs.size, confirmChecked, requiresTextConfirm, deleteConfirmText]);

  const handleDelete = async () => {
    if (!canDelete || isDeleting) return;

    setIsDeleting(true);
    setDeleteProgress(0);

    const jobIds = Array.from(selectedJobs);
    const total = jobIds.length;

    try {
      for (let i = 0; i < total; i++) {
        setDeleteProgress(Math.round(((i + 1) / total) * 100));
      }
      
      await onDeleteJobs(jobIds);
      
      toast.success(`Successfully deleted ${total} jobs and ${totalImages} images`);
      
      // Reset state and close
      setSelectedJobs(new Set());
      setConfirmChecked(false);
      setDeleteConfirmText('');
      onOpenChange(false);
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error('Failed to delete some jobs');
    } finally {
      setIsDeleting(false);
      setDeleteProgress(0);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!isDeleting) {
      if (!newOpen) {
        setSelectedJobs(new Set());
        setConfirmChecked(false);
        setDeleteConfirmText('');
      }
      onOpenChange(newOpen);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Trash2 className="h-5 w-5" />
            Delete Automation Jobs
          </DialogTitle>
          <DialogDescription>
            Select which jobs to delete. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="flex items-center justify-between bg-muted/50 rounded-lg p-3 border">
          <div className="flex items-center gap-4 text-sm">
            <span className="font-medium">
              {selectedJobs.size} {selectedJobs.size === 1 ? 'Job' : 'Jobs'}
            </span>
            <span className="flex items-center gap-1 text-muted-foreground">
              <ImageIcon className="h-4 w-4" />
              {totalImages.toLocaleString()} images
            </span>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={selectAll} disabled={isDeleting}>
              Select All
            </Button>
            {downloadedCount > 0 && (
              <Button size="sm" variant="outline" onClick={selectDownloaded} disabled={isDeleting} className="text-green-600">
                <Download className="h-3 w-3 mr-1" />
                Downloaded ({downloadedCount})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={clearAll} disabled={isDeleting}>
              Clear
            </Button>
          </div>
        </div>

        {/* Warning Banner */}
        {showWarning && selectedJobs.size > 0 && (
          <div className={`flex items-center gap-2 p-3 rounded-lg border ${
            isCritical 
              ? 'bg-destructive/10 border-destructive text-destructive' 
              : 'bg-yellow-500/10 border-yellow-500 text-yellow-700 dark:text-yellow-400'
          }`}>
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="text-sm font-medium">
              {isCritical 
                ? `Critical: Deleting ${selectedJobs.size} jobs will remove ${totalImages.toLocaleString()} images permanently!`
                : `Warning: This will permanently delete ${totalImages.toLocaleString()} images!`
              }
            </span>
          </div>
        )}

        {/* Job List */}
        <ScrollArea className="h-[250px] border rounded-lg">
          <div className="p-2 space-y-1">
            {jobs.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                No jobs available
              </div>
            ) : (
              jobs.map((job) => (
                <label
                  key={job.id}
                  className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                    selectedJobs.has(job.id) 
                      ? 'bg-destructive/10 border border-destructive/30' 
                      : 'hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectedJobs.has(job.id)}
                      onCheckedChange={() => toggleJob(job.id)}
                      disabled={isDeleting}
                    />
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate max-w-[180px]">
                        {job.job_name}
                      </span>
                      {job.downloaded_at && (
                        <Badge variant="outline" className="text-green-600 border-green-600 text-[10px] px-1.5 py-0">
                          <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                          Downloaded
                        </Badge>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {job.images_generated || 0} imgs
                  </span>
                </label>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Text Confirmation for Large Deletions */}
        {requiresTextConfirm && selectedJobs.size > 0 && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-destructive">
              Type DELETE to confirm deletion of {selectedJobs.size} jobs:
            </label>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
              placeholder="Type DELETE"
              disabled={isDeleting}
              className="border-destructive/50 focus:border-destructive"
            />
          </div>
        )}

        {/* Confirmation Checkbox */}
        {selectedJobs.size > 0 && (
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox
              checked={confirmChecked}
              onCheckedChange={(checked) => setConfirmChecked(checked === true)}
              disabled={isDeleting}
            />
            <span className="text-sm">
              I understand this action cannot be undone
            </span>
          </label>
        )}

        {/* Progress Bar */}
        {isDeleting && (
          <div className="space-y-2">
            <Progress value={deleteProgress} className="h-2" />
            <p className="text-sm text-center text-muted-foreground">
              Deleting jobs... {deleteProgress}%
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canDelete || isDeleting}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete {selectedJobs.size} {selectedJobs.size === 1 ? 'Job' : 'Jobs'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
